import { LifecycleManager } from "./_base-lifecycles";

import removeEmptyPTags from "../global/cleanup-p-tags";
import { setCustomProperties, setCustomPropertiesEditor } from "../global/set-css-custom-props";
import smoothScrollAnchorLinks from "../global/smooth-scroll-anchor-links";
import preloadImages from "../global/preload-images";
import { InitCSSHasSelectorHelper } from "../global/has-selector-helper";
import preventLinksInEditor from "../editor/prevent-links-in-editor";
import { InitHeroEditor } from "../editor/hero";
import { InitEditorPageTheme } from "../editor/page-theme";
import { initializeSlider, initializeSliderEditor } from "../components/slider";
import { initializeNav } from "../components/nav";
import { InitMediaBlocks } from "../components/media-block";
import { InitForms } from "../components/forms";
import { initializeAccordions } from "../components/accordion";
import { initializeModals, initializeModalsEditor } from "../components/modal";
import { InitializeAnimatedCollage } from "../components/animated-collage";
import { initializeVideo } from "../components/video";
import { InitImageTicker } from "../components/image-ticker";
import { InitThreeCircleInfographic } from "../components/three-circle-infographic";
import { InitTeamCards } from "../components/team";
import { initNewsLetterModal } from "../components/newslettermodal";
import { InitBackgroundTransitions } from "../components/background-transitions";
import { initializePV } from "../global/init-petite-vue";

import {
  InitializeGSAPOnPageLoad,
  IntializeParallaxBackgrounds,
  IntializeBGLineAnimations,
} from "../utility/init-gsap";
import { InitializeFooterAnimation } from "../utility/animations/components/footer/footerAnimations";

/**************************************************
 Event Definitions
 **************************************************/
const EVENTS = Object.freeze({
  /**
   * This event is called first thing in the editor and main.js files
   * @type {Symbol<'Initialize'>}
   */
  INIT: Symbol("Initialize"),
  /**
   * This event is called when the document is ready in the editor and main.js files
   * @type {Symbol<'Document Ready'>}
   */
  DOCUMENT_READY: Symbol("Document Ready"),
  /**
   * This event is called on the window `load` event in the editor and main.js files
   * @type {Symbol<'Window Load'>}
   */
  WINDOW_LOAD: Symbol("Window Load"),
  /**
   * This event is called once all images have loaded in the main.js file
   * @type {Symbol<'Page Loaded'>}
   */
  PAGE_LOADED: Symbol("Page Loaded"),
});

/**************************************************
   Main Lifecycle
 **************************************************/
const MainLifecycleManager = new LifecycleManager();

MainLifecycleManager.registerEvent(EVENTS.DOCUMENT_READY, InitializeGSAPOnPageLoad)
  .registerEvent(EVENTS.DOCUMENT_READY, initializeAccordions)
  .registerEvent(EVENTS.DOCUMENT_READY, initializeModals)
  .registerEvent(EVENTS.DOCUMENT_READY, initializeVideo)
  .registerEvent(EVENTS.DOCUMENT_READY, InitBackgroundTransitions)
  .registerEvent(EVENTS.DOCUMENT_READY, removeEmptyPTags)
  .registerEvent(EVENTS.DOCUMENT_READY, setCustomProperties)
  .registerEvent(EVENTS.DOCUMENT_READY, smoothScrollAnchorLinks)
  .registerEvent(EVENTS.DOCUMENT_READY, InitImageTicker)
  .registerEvent(EVENTS.DOCUMENT_READY, initNewsLetterModal)
  .registerEvent(EVENTS.DOCUMENT_READY, InitializeFooterAnimation)
  .registerEvent(EVENTS.DOCUMENT_READY, InitForms)
  .registerEvent(EVENTS.DOCUMENT_READY, InitCSSHasSelectorHelper)
  .registerEvent(EVENTS.DOCUMENT_READY, InitThreeCircleInfographic)
  .registerEvent(EVENTS.DOCUMENT_READY, InitTeamCards)
  .registerEvent(EVENTS.DOCUMENT_READY, InitMediaBlocks)
  .registerEvent(EVENTS.DOCUMENT_READY, initializeNav);

MainLifecycleManager.registerEvent(EVENTS.WINDOW_LOAD, initializeSlider)
  .registerEvent(EVENTS.WINDOW_LOAD, preloadImages)
  .registerEvent(EVENTS.WINDOW_LOAD, initializePV)
  .registerEvent(EVENTS.WINDOW_LOAD, IntializeBGLineAnimations);

MainLifecycleManager.registerEvent(EVENTS.PAGE_LOADED, IntializeParallaxBackgrounds).registerEvent(
  EVENTS.PAGE_LOADED,
  InitializeAnimatedCollage
);

/**************************************************
 Editor Lifecycle
 **************************************************/
const EditorLifecycleManager = new LifecycleManager();

EditorLifecycleManager.registerEvent(EVENTS.INIT, initializeSliderEditor);

EditorLifecycleManager.registerEvent(EVENTS.DOCUMENT_READY, initializeModalsEditor)
  .registerEvent(EVENTS.DOCUMENT_READY, initializeAccordions)
  .registerEvent(EVENTS.DOCUMENT_READY, InitHeroEditor)
  .registerEvent(EVENTS.DOCUMENT_READY, InitEditorPageTheme)
  .registerEvent(EVENTS.DOCUMENT_READY, preventLinksInEditor)
  .registerEvent(EVENTS.DOCUMENT_READY, InitCSSHasSelectorHelper)
  .registerEvent(EVENTS.DOCUMENT_READY, setCustomPropertiesEditor);

export { EVENTS, MainLifecycleManager, EditorLifecycleManager };
